import { Link } from 'react-router';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { FunnelChart } from '@/components/funnel/funnel-chart';
import { TagBadge } from '@/components/tags/tag-badge';
import { useCarmaProgression } from '@/hooks/use-funnel';
import { useTags, useTriggerSweep } from '@/hooks/use-tags';
import { useAuthContext } from '@/contexts/auth-context';
import { cn } from '@/lib/utils';
import type { Tag } from '@/types/tags';

export default function FunnelPage() {
  const { user } = useAuthContext();
  const { data: stages, isLoading: stagesLoading } = useCarmaProgression();
  const { data: segments = [], isLoading: segmentsLoading } = useTags({ kind: 'segment' });
  const { data: milestones = [], isLoading: milestonesLoading } = useTags({ kind: 'milestone' });
  const triggerSweep = useTriggerSweep();

  const runSweep = async () => {
    try {
      await triggerSweep.mutateAsync();
      toast.success('Nightly sweep enqueued. The worker will pick it up within ~30s.');
    } catch {
      toast.error('Failed to enqueue sweep.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Carma progression</h1>
        {user?.role === 'admin' && (
          <Button
            variant="outline"
            size="sm"
            onClick={runSweep}
            disabled={triggerSweep.isPending}
          >
            {triggerSweep.isPending ? 'Running…' : 'Run sweep now'}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Stage distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {stagesLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <FunnelChart stages={stages ?? []} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Stages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(stages ?? []).map((s) => (
              <Link
                key={s.id}
                to={`/students?tag_id=${s.id}`}
                className={cn(
                  'block rounded-md border p-3 transition-colors',
                  'hover:border-primary hover:bg-accent/50',
                )}
                title={`View students in stage ${s.name}`}
              >
                <p className="font-medium">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.slug}</p>
                <p className="mt-2 text-2xl font-semibold">{s.user_count}</p>
                <p className="text-xs text-muted-foreground">students in this stage</p>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      <TagCohortSection
        title="Segments"
        description="Rule-driven cohorts evaluated on every event and the nightly sweep. Membership is dynamic."
        tags={segments}
        isLoading={segmentsLoading}
      />

      <TagCohortSection
        title="Milestones"
        description="Permanent achievements. Once earned they are never revoked, even if the rule later stops matching."
        tags={milestones}
        isLoading={milestonesLoading}
      />
    </div>
  );
}

interface TagCohortSectionProps {
  title: string;
  description: string;
  tags: Tag[];
  isLoading: boolean;
}

function TagCohortSection({ title, description, tags, isLoading }: TagCohortSectionProps) {
  const total = tags.reduce((acc, tag) => acc + (tag.assignment_count ?? 0), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{title}</span>
          <span className="text-sm font-normal text-muted-foreground">
            {total} total membership{total === 1 ? '' : 's'}
          </span>
        </CardTitle>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : tags.length === 0 ? (
          <p className="text-sm text-muted-foreground">None defined yet.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {tags.map((tag) => {
              const count = tag.assignment_count ?? 0;
              const card = (
                <div
                  key={tag.id}
                  className={cn(
                    'rounded-md border p-3',
                    count > 0 && 'transition-colors hover:border-primary hover:bg-accent/50',
                  )}
                >
                  <div className="mb-2">
                    <TagBadge tag={tag} />
                  </div>
                  <p className="text-xs text-muted-foreground">{tag.slug}</p>
                  <p className="mt-2 text-2xl font-semibold">{count}</p>
                  <p className="text-xs text-muted-foreground">students</p>
                </div>
              );
              if (count === 0) {
                return <div key={tag.id}>{card}</div>;
              }
              return (
                <Link
                  key={tag.id}
                  to={`/students?tag_id=${tag.id}`}
                  className="block"
                  title={`View students with ${tag.name}`}
                >
                  {card}
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
